import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Anchor, Divider, Select, TextInput } from '@mantine/core';
import { Template } from 'tabler-icons-react';
import EditProductCard from './EditProductCard';
import { Context as AdminContext } from '../../../providers/AdminContextProvider';
import CustomSelect from '../../common/CustomSelect';
import AppText from '../../common/AppText';
import AssignPrintTemplateModal from './AssignPrintTemplateModal';
import { useModalState } from '../../../helpers/hooks';
import AppStack from '../../common/AppStack';
import AppCard from '../../common/AppCard';
import AppFlexbox from '../../common/AppFlexbox';
import AppImage from '../../common/AppImage';
import {
  CURRENCY_TYPE_LIST,
  ECOM_IMAGE_POSITION_LIST
} from '../../../config/constants';
import { downloadUrlFile } from '../../../helpers/awsHelper';
import { EXTERNAL_CONNECTION_CONFIGS } from '../external/config/externalConnectionConfigs';
import { PRODUCT_CATEGORY_LIST } from '../../../config/productCategories';
import AppCheckbox from '../../common/AppCheckbox';
import CustomNumericFormat from '../../common/CustomNumericFormat';
import CustomNumberInput from '../../common/CustomNumberInput';

const EcomExternalProductEditControls = ({
  externalConfig,
  formState,
  setFormState,
  isSubmitting
}) => {
  const { state: modalState, onOpenModal, onCloseModal } = useModalState();
  const { state, fetchEcomExternalProductCreateOptions } = useContext(
    AdminContext
  );

  const productCreateOptions = state.ecomExternalProductCreateOptions.value.find(
    (v) =>
      v.pkEcomExternalConnectionType.toString() ===
      externalConfig?.pkEcomExternalConnectionType.toString()
  );

  const variantSizeOptions = formState.ecomVendorProductVariants
    .map((v) =>
      v.ecomVendorProductVariantOptions
        .filter((op) => op.name?.toLowerCase() === 'size')
        .map((op) => ({
          label: op.value,
          value: op.value
        }))
    )
    .flat()
    .reduce((r, c) => {
      if (!r.find((o) => o.value === c.value)) {
        r.push(c);
      }
      return r;
    }, []);

  const variantSizeOptionsWithPrintTemplates = variantSizeOptions.filter((s) =>
    formState.productDesigns.some(
      (t) => t.sizeOption === s.value && !t.isPreviewTemplate
    )
  );
  const previewTemplates = formState.productDesigns.filter(
    (t) => t.isPreviewTemplate
  );

  useEffect(() => {
    if (externalConfig?.products?.inputSections && !productCreateOptions) {
      fetchEcomExternalProductCreateOptions(
        externalConfig.pkEcomExternalConnectionType
      );
    }
  }, [externalConfig]);

  return (
    <>
      <EditProductCard title="Provider">
        <Select
          clearable
          data={EXTERNAL_CONNECTION_CONFIGS.map((c) => ({
            label: c.title,
            value: c.pkEcomExternalConnectionType.toString()
          }))}
          disabled={!!formState.pkEcomExternalProduct || isSubmitting}
          label="External Provider"
          onChange={(value) => {
            setFormState({ ...formState, fkEcomExternalConnectionType: value });
          }}
          required
          searchable
          value={formState.fkEcomExternalConnectionType?.toString() || null}
        />

        <TextInput
          disabled
          label="External ID"
          onChange={(e) => {
            setFormState({ ...formState, externalId: e.currentTarget.value });
          }}
          value={formState.externalId || ''}
        />

        <TextInput
          disabled
          label="Model"
          onChange={() => {}}
          value={formState.model || ''}
        />

        <AppCheckbox
          checked={formState.usesGarmentCanvas || false}
          description="Render design on full-size garment canvas instead of product-specific dimensions."
          disabled={isSubmitting}
          label="Uses garment canvas"
          onChange={() =>
            setFormState({
              ...formState,
              usesGarmentCanvas: !formState.usesGarmentCanvas
            })
          }
          styles={{
            description: { fontSize: 13 },
            label: { fontWeight: 500 }
          }}
        />
      </EditProductCard>

      {externalConfig?.products?.inputSections &&
        externalConfig.products.inputSections.map((inputSection) => (
          <EditProductCard key={inputSection.label} title={inputSection.label}>
            {inputSection.inputs.map((input) => {
              const { dataValue } = input;
              const value = formState[dataValue] || '';
              const productCreateData = productCreateOptions?.options.find(
                (op) => op.dataValue === dataValue
              );
              const selectedOption = productCreateData?.options.find(
                (op) => op.value === value
              );
              const previewLink = input.getNavLink
                ? input.getNavLink(selectedOption)
                : null;

              return (
                <AppStack key={dataValue} style={{ gap: 5 }}>
                  {input.type === 'select' ? (
                    <CustomSelect
                      data={
                        productCreateData?.options
                          .map((op) => ({
                            forceImage: true,
                            image: op.image,
                            label: op.label,
                            description: op.description,
                            value: op.value
                          }))
                          .sort((a, b) => a.label.localeCompare(b.label)) || []
                      }
                      description={input.description}
                      disabled={
                        isSubmitting ||
                        state.ecomExternalProductCreateOptions.loading
                      }
                      label={input.label}
                      onChange={(v) =>
                        setFormState({
                          ...formState,
                          [dataValue]: v
                        })
                      }
                      required={input.required}
                      value={value}
                    />
                  ) : (
                    <TextInput
                      description={input.description}
                      disabled={isSubmitting}
                      label={input.label}
                      onChange={(e) =>
                        setFormState({
                          ...formState,
                          [dataValue]: e.currentTarget.value
                        })
                      }
                      required={input.required}
                      value={value}
                    />
                  )}
                  {previewLink && (
                    <Anchor
                      href={previewLink}
                      style={{ fontSize: 12, color: 'dodgerblue' }}
                      target="_blank"
                    >
                      {input.linkTitle}
                    </Anchor>
                  )}
                </AppStack>
              );
            })}
          </EditProductCard>
        ))}

      <EditProductCard title="Product organization">
        <Select
          clearable
          data={PRODUCT_CATEGORY_LIST.sort((a, b) =>
            a.name.localeCompare(b.name)
          ).map((c) => ({
            value: c.value.toString(),
            label: c.name
          }))}
          disabled={isSubmitting}
          label="Category"
          onChange={(value) =>
            setFormState({
              ...formState,
              fkEcomCategory: value
            })
          }
          searchable
          value={formState.fkEcomCategory}
        />
        <TextInput
          disabled={isSubmitting}
          label="Sub category"
          onChange={(e) =>
            setFormState({
              ...formState,
              productType: e.currentTarget.value
            })
          }
          value={formState.productType ?? ''}
        />
      </EditProductCard>

      {externalConfig && (
        <EditProductCard title="Pricing">
          <CustomNumberInput
            allowNegative={false}
            decimalScale={2}
            description="Add a percentage markup to the base cost of the product, adjusting the price for all vendors."
            disabled={isSubmitting}
            fixedDecimalScale
            label={`Markup (in '%')`}
            onChange={(v) =>
              setFormState({
                ...formState,
                markupPercentage: v
              })
            }
            rightSection={
              <AppText
                style={{
                  fontWeight: 500,
                  color: '#666',
                  fontSize: 14
                }}
              >
                %
              </AppText>
            }
            value={formState.markupPercentage}
          />

          <Select
            clearable
            data={CURRENCY_TYPE_LIST.map((c) => ({
              label: c.fullLabel,
              value: c.value.toString()
            }))}
            disabled
            label="Currency"
            onChange={(value) => {}}
            searchable
            value={externalConfig?.currencyType?.toString() || null}
          />
        </EditProductCard>
      )}

      {previewTemplates && previewTemplates.length > 0 && (
        <EditProductCard
          title="Printify template"
          titleAction={
            <Anchor
              onClick={() => {
                onOpenModal('manage_print_templates');
              }}
              style={{ color: 'dodgerblue', fontSize: 14 }}
            >
              Manage
            </Anchor>
          }
        >
          <AppStack style={{ gap: 5 }}>
            <AppText style={{ fontSize: 14, color: '#666' }}>
              Used to generate product previews.
            </AppText>

            <AppCard radius={8} showow="none" style={{ padding: 0 }} withBorder>
              <AppStack style={{ gap: 0 }}>
                <AppStack style={{ gap: 0 }}>
                  {previewTemplates.map((t, index) => (
                    <React.Fragment
                      key={t.pkEcomExternalProductPrintTemplate || t.key}
                    >
                      {index > 0 && <Divider />}
                      <AppFlexbox
                        style={{
                          padding: 8,
                          gap: 8,
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
                          <AppStack
                            style={{
                              width: 50,
                              height: 50,
                              overflow: 'hidden',
                              border: 'solid 1px lightgrey',
                              borderRadius: 4,
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <Template color="#999" size={32} />
                          </AppStack>
                          <AppStack style={{ gap: 0 }}>
                            <AppText
                              style={{
                                fontSize: 14,
                                fontWeight: 500
                              }}
                            >
                              {ECOM_IMAGE_POSITION_LIST.find(
                                (pos) =>
                                  pos.value.toString() ===
                                  t.fkEcomImagePosition.toString()
                              )?.label || 'Unknown Position'}
                            </AppText>
                            <AppText
                              style={{
                                fontSize: 14,
                                color:
                                  !t.imageWidthPx || !t.imageHeightPx
                                    ? '#C40000'
                                    : '#000'
                              }}
                            >
                              {!t.imageWidthPx || !t.imageHeightPx
                                ? 'Missing dimensions'
                                : `${t.imageWidthPx}x${t.imageHeightPx}px`}
                            </AppText>
                          </AppStack>
                        </AppFlexbox>
                      </AppFlexbox>
                    </React.Fragment>
                  ))}
                </AppStack>
              </AppStack>
            </AppCard>
          </AppStack>
        </EditProductCard>
      )}

      {externalConfig?.products.restrictions.canCustomizeDesign && (
        <EditProductCard
          title="Print templates"
          titleAction={
            <Anchor
              onClick={() => {
                onOpenModal('manage_print_templates');
              }}
              style={{ color: 'dodgerblue', fontSize: 14 }}
            >
              Manage
            </Anchor>
          }
        >
          {variantSizeOptionsWithPrintTemplates.length === 0 ? (
            <AppText style={{ fontSize: 14, color: '#666' }}>
              No print templates assigned.
            </AppText>
          ) : (
            <AppStack style={{ gap: 5 }}>
              {variantSizeOptionsWithPrintTemplates.map((s) => {
                const templates = formState.productDesigns.filter(
                  (t) => t.sizeOption === s.value && !t.isPreviewTemplate
                );

                return (
                  <AppCard
                    key={s.value}
                    radius={8}
                    showow="none"
                    style={{ padding: 0 }}
                    withBorder
                  >
                    <AppStack style={{ gap: 0 }}>
                      <AppStack style={{ padding: 8 }}>
                        <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                          {s.label}
                        </AppText>
                      </AppStack>
                      <Divider />
                      <AppStack style={{ gap: 0 }}>
                        {templates.map((t, index) => {
                          const templateImageUrl =
                            t.imagePreview || t.svgSource;
                          return (
                            <React.Fragment
                              key={
                                t.pkEcomExternalProductPrintTemplate || t.key
                              }
                            >
                              {index > 0 && <Divider />}
                              <AppFlexbox
                                style={{
                                  padding: 8,
                                  gap: 8,
                                  alignItems: 'center',
                                  justifyContent: 'space-between'
                                }}
                              >
                                <AppFlexbox
                                  style={{ gap: 8, alignItems: 'center' }}
                                >
                                  <AppStack
                                    style={{
                                      width: 50,
                                      height: 50,
                                      overflow: 'hidden',
                                      border: 'solid 1px lightgrey',
                                      borderRadius: 4,
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    {templateImageUrl ? (
                                      <AppImage
                                        height="100%"
                                        src={templateImageUrl}
                                        width="100%"
                                      />
                                    ) : (
                                      <Template color="#999" size={32} />
                                    )}
                                  </AppStack>
                                  <AppStack style={{ gap: 0 }}>
                                    <AppText
                                      style={{
                                        fontSize: 14,
                                        fontWeight: 500
                                      }}
                                    >
                                      {ECOM_IMAGE_POSITION_LIST.find(
                                        (pos) =>
                                          pos.value.toString() ===
                                          t.fkEcomImagePosition.toString()
                                      )?.label || 'Unknown Position'}
                                    </AppText>
                                    <AppText
                                      style={{
                                        fontSize: 14,
                                        color:
                                          !t.imageWidthPx || !t.imageHeightPx
                                            ? '#C40000'
                                            : '#000'
                                      }}
                                    >
                                      {!t.imageWidthPx || !t.imageHeightPx
                                        ? 'Missing dimensions'
                                        : `${t.imageWidthPx}x${t.imageHeightPx}px`}
                                    </AppText>
                                    {t.isDefault && (
                                      <AppText
                                        style={{
                                          fontSize: 14,
                                          color: '#666'
                                        }}
                                      >
                                        Default
                                      </AppText>
                                    )}
                                    {templateImageUrl && (
                                      <Anchor
                                        onClick={() => {
                                          const fileName = templateImageUrl
                                            .split('/')
                                            .pop();
                                          downloadUrlFile(
                                            templateImageUrl,
                                            fileName
                                          );
                                        }}
                                        style={{
                                          fontSize: 12,
                                          color: 'dodgerblue'
                                        }}
                                      >
                                        Download template
                                      </Anchor>
                                    )}
                                  </AppStack>
                                </AppFlexbox>
                              </AppFlexbox>
                            </React.Fragment>
                          );
                        })}
                      </AppStack>
                    </AppStack>
                  </AppCard>
                );
              })}
            </AppStack>
          )}
        </EditProductCard>
      )}

      <AssignPrintTemplateModal
        isOpen={
          modalState.isOpen && modalState.action === 'manage_print_templates'
        }
        onClose={onCloseModal}
        onConfirm={(productDesigns) => {
          setFormState({ ...formState, productDesigns });
          onCloseModal();
        }}
        productDesigns={formState.productDesigns}
        sizeOptions={variantSizeOptions}
      />
    </>
  );
};

EcomExternalProductEditControls.propTypes = {
  formState: PropTypes.object.isRequired,
  setFormState: PropTypes.func.isRequired,
  externalConfig: PropTypes.object,
  isSubmitting: PropTypes.bool
};

export default EcomExternalProductEditControls;
